import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, ArrowRight, ArrowLeft, CheckCircle2, LayoutDashboard,
  Bell, MessageSquare, BookOpen, ListChecks, Users, FileText, UserCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * 5-step orientation walkthrough for newly-invited employees.
 * Shows a visual preview of what's where in the platform, then drops them on the dashboard.
 */
export default function WelcomeOrientationPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [team, setTeam] = useState(null);

  const firstName = useMemo(() => (user?.full_name || '').split(' ')[0] || '', [user]);
  const businessName = team?.business_name || user?.business_name || 'your team';

  // Fetch team snapshot for the "Meet Your Team" step.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/staff/my-team`);
        setTeam(data);
      } catch {
        setTeam(null);
      }
    })();
  }, []);

  const steps = useMemo(() => ([
    {
      icon: Sparkles,
      title: `You're in, ${firstName || 'welcome'}!`,
      subtitle: `Joining ${businessName} on Konnect.`,
      body: 'In about 60 seconds we\'ll show you exactly where to find what you need. Tap Next to begin.',
      visual: 'hero',
    },
    {
      icon: Users,
      title: 'Meet your team',
      subtitle: team?.manager
        ? `${team.manager.full_name || 'Your manager'} invited you. Here\'s who else is on the crew.`
        : 'Here\'s who you\'ll be working with.',
      body: team?.first_task || 'Say hello in your team chat — the fastest way to feel at home is to make the first move.',
      visual: 'team',
      teamData: team,
    },
    {
      icon: LayoutDashboard,
      title: 'Your Dashboard',
      subtitle: 'Everything starts from one screen.',
      body: 'Your dashboard shows tasks, messages, recent activity, and quick links to the tools your role uses every day.',
      visual: 'dashboard',
      bullets: ['Tasks assigned to you', 'New messages & mentions', 'Today\'s schedule', 'Pinned actions for your role'],
    },
    {
      icon: ListChecks,
      title: 'Tasks & Operations',
      subtitle: 'How work flows to you.',
      body: 'Your manager assigns tasks. Each task has a deadline, attachments, and a comment thread. Move tasks across the board as you work.',
      visual: 'tasks',
      bullets: ['Drag-and-drop status board', 'Comments + file attachments', 'Mobile push reminders', 'Time tracking on each task'],
    },
    {
      icon: MessageSquare,
      title: 'Messages, Help & Tours',
      subtitle: 'You\'re never stuck.',
      body: 'Use the chat icon to message teammates. Open the help menu or take a guided mini-tour any time you visit a new section.',
      visual: 'support',
      bullets: ['Built-in team chat', 'Mini-tours on every page', 'Searchable help center', '24/7 ticket support'],
    },
  ]), [firstName, businessName, team]);

  const finish = async () => {
    try {
      await axios.post(`${API}/api/auth/orientation/complete`, {});
      await refreshUser();
    } catch {
      // Silent — orientation flag is best-effort.
    }
    toast.success('Welcome aboard! 🎉');
    navigate('/dashboard', { replace: true });
  };

  const next = () => {
    if (step < steps.length - 1) setStep(s => s + 1);
    else finish();
  };
  const back = () => setStep(s => Math.max(0, s - 1));
  const skip = () => finish();

  const cur = steps[step];
  const Icon = cur.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white flex items-center justify-center p-4">
      <div className="max-w-2xl w-full" data-testid="welcome-orientation">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1.5 mb-5">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? 'bg-emerald-400 w-8' : i < step ? 'bg-emerald-700 w-4' : 'bg-slate-700 w-4'}`}
              data-testid={`step-dot-${i}`}
            />
          ))}
        </div>

        <div className="bg-slate-900/60 backdrop-blur-md rounded-3xl border border-slate-800 p-8 sm:p-10 shadow-2xl">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 shrink-0">
              <Icon className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid={`orientation-title-${step}`}>{cur.title}</h1>
              <p className="text-sm text-emerald-300 mt-1">{cur.subtitle}</p>
            </div>
          </div>

          <p className="text-base text-slate-300 leading-relaxed mb-5">{cur.body}</p>

          {cur.bullets && (
            <ul className="space-y-2 mb-6">
              {cur.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Visual preview */}
          {cur.visual === 'team' && (
            <div className="rounded-2xl bg-slate-950/60 border border-slate-800 p-4 mb-6 space-y-3" data-testid="orientation-team-visual">
              {cur.teamData?.manager && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  {cur.teamData.manager.avatar_url ? (
                    <img src={cur.teamData.manager.avatar_url} alt="manager" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                      {(cur.teamData.manager.full_name || '?').split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white truncate">{cur.teamData.manager.full_name}</div>
                    <div className="text-[10px] text-emerald-300 uppercase tracking-widest">Invited you</div>
                  </div>
                </div>
              )}
              {cur.teamData?.teammate_count > 0 ? (
                <>
                  <div className="text-[11px] text-slate-400 uppercase tracking-widest">
                    Teammates ({cur.teamData.teammate_count})
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {(cur.teamData.teammates || []).slice(0, 8).map((t) => (
                      <div key={t.id} className="text-center" title={`${t.first_name} ${t.last_name} — ${t.job_title || t.department}`}>
                        {t.avatar_url ? (
                          <img src={t.avatar_url} alt={t.first_name} className="h-9 w-9 rounded-full object-cover mx-auto" />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 text-xs mx-auto">
                            {(t.first_name?.[0] || '?') + (t.last_name?.[0] || '')}
                          </div>
                        )}
                        <div className="text-[9px] text-slate-400 mt-0.5 truncate">{t.first_name}</div>
                      </div>
                    ))}
                    {cur.teamData.teammate_count > 8 && (
                      <div className="text-center">
                        <div className="h-9 w-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 text-[10px] mx-auto">
                          +{cur.teamData.teammate_count - 8}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-3 rounded-lg bg-slate-900 border border-slate-800 text-center">
                  <UserCircle className="h-8 w-8 text-slate-600 mx-auto mb-1" />
                  <div className="text-[11px] text-slate-500">You&apos;re the first here. More teammates will appear as they join.</div>
                </div>
              )}
            </div>
          )}
          {cur.visual === 'hero' && (
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 p-6 mb-6 text-center">
              <Sparkles className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
              <p className="text-xs text-slate-400">Your account is fully set up. We&apos;re just walking you through the basics.</p>
            </div>
          )}
          {cur.visual === 'dashboard' && (
            <div className="rounded-2xl bg-slate-950/60 border border-slate-800 p-4 mb-6 grid grid-cols-3 gap-2 text-center">
              {[
                { icon: ListChecks, label: 'Tasks' },
                { icon: Bell, label: 'Alerts' },
                { icon: FileText, label: 'Docs' },
              ].map(({ icon: I, label }, i) => (
                <div key={i} className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                  <I className="h-5 w-5 text-emerald-400 mx-auto mb-1" />
                  <p className="text-[11px] text-slate-400">{label}</p>
                </div>
              ))}
            </div>
          )}
          {cur.visual === 'tasks' && (
            <div className="rounded-2xl bg-slate-950/60 border border-slate-800 p-3 mb-6 grid grid-cols-3 gap-2">
              {['To Do', 'In Progress', 'Done'].map((col, i) => (
                <div key={i} className="bg-slate-900 rounded-lg p-2 border border-slate-800">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">{col}</p>
                  <div className="space-y-1">
                    {[0, 1].map(j => (
                      <div key={j} className="h-6 rounded bg-slate-800/60 border border-slate-700/40" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {cur.visual === 'support' && (
            <div className="rounded-2xl bg-slate-950/60 border border-slate-800 p-4 mb-6 flex items-center justify-around">
              <div className="text-center">
                <MessageSquare className="h-7 w-7 text-emerald-400 mx-auto mb-1" />
                <p className="text-[10px] text-slate-400">Chat</p>
              </div>
              <div className="text-center">
                <BookOpen className="h-7 w-7 text-emerald-400 mx-auto mb-1" />
                <p className="text-[10px] text-slate-400">Help</p>
              </div>
              <div className="text-center">
                <Users className="h-7 w-7 text-emerald-400 mx-auto mb-1" />
                <p className="text-[10px] text-slate-400">Team</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-800">
            <Button
              variant="ghost"
              onClick={back}
              disabled={step === 0}
              className="text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30"
              data-testid="orientation-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>

            <button
              type="button"
              onClick={skip}
              className="text-xs text-slate-500 hover:text-slate-300 underline-offset-2 hover:underline"
              data-testid="orientation-skip-btn"
            >
              Skip orientation
            </button>

            <Button
              onClick={next}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="orientation-next-btn"
            >
              {step === steps.length - 1 ? <>Enter dashboard <ArrowRight className="h-4 w-4 ml-1.5" /></> : <>Next <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-4">
          Step {step + 1} of {steps.length}
        </p>
      </div>
    </div>
  );
}
